import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { brandsKey, useBrands, useWebhookStatus } from '../hooks/useMeta';
import type { BrandDto, WebhookStatusDto } from '@shared/types';

/** Textová pole CRM — číselná mají vlastní blok níž, aby se neposílala jako string. */
const CRM_TEXT_FIELDS: Array<{ key: 'crmTitlePrefix' | 'crmCreatedBy' | 'crmChannelLabel'; label: string; hint?: string }> = [
  { key: 'crmTitlePrefix', label: 'Prefix názvu', hint: 'Např. „Meta lead —"' },
  { key: 'crmCreatedBy', label: 'Vytvořil (crmCreatedBy)' },
  { key: 'crmChannelLabel', label: 'Označení kanálu' },
];

const CRM_NUMBER_FIELDS: Array<{ key: 'crmStatusId' | 'crmMktChannel' | 'crmRegion'; label: string }> = [
  { key: 'crmStatusId', label: 'ID stavu' },
  { key: 'crmMktChannel', label: 'Marketingový kanál' },
  { key: 'crmRegion', label: 'Region (výchozí)' },
];

/** Jazyky formulářů (stejné jako výběr „Jazyk komunikace“ u formuláře). */
const REGION_LOCALES: Array<{ value: string; label: string }> = [
  { value: 'cs', label: 'Čeština' },
  { value: 'sk', label: 'Slovenština' },
  { value: 'en', label: 'Angličtina' },
  { value: 'de', label: 'Němčina' },
  { value: 'pl', label: 'Polština' },
  { value: 'hu', label: 'Maďarština' },
];

const CONFIG_FLAGS: Array<{ key: keyof WebhookStatusDto; label: string }> = [
  { key: 'crmTokenSet', label: 'CRM token' },
  { key: 'resendKeySet', label: 'Resend API klíč (e-maily)' },
  { key: 'whatchimpKeySet', label: 'Whatchimp API klíč (WhatsApp)' },
  { key: 'globalTokenSet', label: 'Meta System User token' },
  { key: 'appSecretSet', label: 'Meta App Secret' },
  { key: 'verifyTokenSet', label: 'Meta Verify Token' },
];

export default function Settings() {
  const { data: brands = [], isLoading } = useBrands();

  if (isLoading) return <div className="card p-8 text-sm text-gray-500">Načítám…</div>;

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-dark">Nastavení</h1>

      {brands.length === 0 ? (
        <div className="card p-8 text-sm text-gray-500">Žádné značky k nastavení.</div>
      ) : (
        brands.map((brand) => <BrandSection key={brand.id} brand={brand} />)
      )}

      <ConfigStatusCard />
    </div>
  );
}

function BrandSection({ brand }: { brand: BrandDto }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<BrandDto>(brand);

  // JSON kvůli crmRegionByLocale — objekt z nového fetch je jiná reference i se stejným obsahem.
  const dirtyKeys = (Object.keys(draft) as Array<keyof BrandDto>).filter(
    (key) => JSON.stringify(draft[key]) !== JSON.stringify(brand[key]),
  );
  const dirty = dirtyKeys.length > 0;

  const save = useMutation({
    mutationFn: () => {
      const patch: Partial<BrandDto> = {};
      for (const key of dirtyKeys) {
        // Přiřazení přes cast — TS neumí spojit dynamický klíč s typem hodnoty.
        (patch as Record<string, unknown>)[key] = draft[key];
      }
      return api.put<BrandDto>(`/brands/${brand.id}`, patch);
    },
    onSuccess: (updated) => {
      setDraft(updated);
      qc.invalidateQueries({ queryKey: brandsKey });
    },
  });

  const set = <K extends keyof BrandDto>(key: K, value: BrandDto[K]) =>
    setDraft({ ...draft, [key]: value });

  /** Prázdné pole = jazyk bez vlastního regionu (použije se výchozí). */
  const setLocaleRegion = (locale: string, raw: string) => {
    const next = { ...draft.crmRegionByLocale };
    const n = Number(raw);
    if (raw.trim() && Number.isInteger(n) && n > 0) next[locale] = n;
    else delete next[locale];
    set('crmRegionByLocale', next);
  };

  return (
    <section className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-dark">
          <span
            className="inline-block h-3 w-3 rounded-full border border-gray-200"
            style={{ backgroundColor: draft.color }}
          />
          {brand.name}
        </h2>
        <button className="btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Ukládám…' : 'Uložit'}
        </button>
      </div>

      {save.isError && (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {(save.error as Error).message}
        </div>
      )}
      {save.isSuccess && !save.isPending && !dirty && (
        <div className="rounded-md border border-positive/40 bg-positive-bg p-3 text-sm text-positive">
          Uloženo.
        </div>
      )}

      <div>
        <div className="label">CRM parametry</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CRM_TEXT_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="label">{f.label}</span>
              <input
                className="input"
                value={draft[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
              />
              {f.hint && <span className="mt-1 block text-xs text-gray-500">{f.hint}</span>}
            </label>
          ))}
          {CRM_NUMBER_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="label">{f.label}</span>
              <input
                type="number"
                className="input"
                value={draft[f.key]}
                onChange={(e) => set(f.key, Number(e.target.value))}
              />
            </label>
          ))}
        </div>
      </div>

      <div>
        <div className="label">Region v CRM podle jazyka formuláře</div>
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {REGION_LOCALES.map((l) => (
            <label key={l.value} className="block">
              <span className="label">{l.label}</span>
              <input
                type="number"
                min={1}
                className="input"
                placeholder={String(draft.crmRegion)}
                value={draft.crmRegionByLocale[l.value] ?? ''}
                onChange={(e) => setLocaleRegion(l.value, e.target.value)}
              />
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          ID regionu v CRM pro leady z formulářů v daném jazyce. Prázdné pole = výchozí region ({draft.crmRegion}).
        </p>
      </div>

      <div>
        <div className="label">E-mail</div>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="label">Odesílatel (From)</span>
            <input
              className="input"
              placeholder="BuyDubai &lt;info@buydubai.eu&gt;"
              value={draft.emailFrom}
              onChange={(e) => set('emailFrom', e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Odpovědi (Reply-To)</span>
            <input
              className="input"
              value={draft.replyTo}
              onChange={(e) => set('replyTo', e.target.value)}
            />
          </label>
          <label className="block">
            <span className="label">Barva značky</span>
            <div className="flex items-center gap-2">
              <input
                type="color"
                className="h-10 w-12 cursor-pointer rounded-md border border-gray-200"
                value={/^#[0-9a-fA-F]{6}$/.test(draft.color) ? draft.color : '#0c7d99'}
                onChange={(e) => set('color', e.target.value)}
              />
              <input
                className="input font-mono text-xs"
                value={draft.color}
                onChange={(e) => set('color', e.target.value)}
              />
            </div>
          </label>
        </div>
      </div>
    </section>
  );
}

function ConfigStatusCard() {
  const { data, isLoading } = useWebhookStatus();

  return (
    <section className="card space-y-3 p-5">
      <h2 className="font-heading text-lg font-semibold text-dark">Stav konfigurace</h2>
      {isLoading ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : !data ? (
        <div className="text-sm text-gray-500">Stav konfigurace se nepodařilo načíst.</div>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {CONFIG_FLAGS.map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${
                    data[key] ? 'bg-positive' : 'bg-danger'
                  }`}
                />
                <span className="text-gray-700">{label}</span>
                <span className="ml-auto text-xs text-gray-500">
                  {data[key] ? 'nastaveno' : 'chybí'}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            Tokeny a API klíče se nastavují jako secrets workeru (wrangler secret), ne tady.
          </p>
        </>
      )}
    </section>
  );
}
